import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { JerseyBadge } from "@/components/Shared";
import ScheduleItinerary from "@/components/ScheduleItinerary";

function StatusBadge({ status, isFinal }) {
  if (status === "live")
    return <span className="bg-[#E63946] text-white text-[10px] font-black uppercase px-2 py-0.5 tracking-widest">● In The Water</span>;
  if (status === "done")
    return <span className="bg-black text-white text-[10px] font-black uppercase px-2 py-0.5 tracking-widest">Done</span>;
  return <span className="brutal-2 text-gray-500 text-[10px] font-black uppercase px-2 py-0.5 tracking-widest">Upcoming</span>;
}

export default function Draw() {
  const [data, setData] = useState({ divisions: [] });
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    const d = await api.schedule();
    setData(d);
    setTab((prev) => prev || (d.divisions[0] && d.divisions[0].id));
    setLoading(false);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <div className="p-8 text-center font-display uppercase tracking-widest text-gray-400">Loading…</div>;

  const active = data.divisions.find((d) => d.id === tab);

  const q = search.trim().toLowerCase();
  const matches = [];
  if (q) {
    data.divisions.forEach((d) =>
      (d.rounds || []).forEach((rnd) =>
        (rnd.heats || []).forEach((h) =>
          (h.riders || []).forEach((r) => {
            if (r.name.toLowerCase().includes(q))
              matches.push({ division: d.name, round: rnd.name, heat: h.name, jersey: r.jersey, name: r.name, status: h.status });
          })
        )
      )
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 pb-28" data-testid="draw-view">
      <h1 className="font-display text-4xl font-black uppercase tracking-tight mb-1">Heat Draw</h1>
      <p className="font-display text-gray-500 uppercase text-xs tracking-widest mb-4">Running order &amp; heat schedule</p>

      <input
        data-testid="draw-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 Find my heat — type a name"
        className="w-full brutal-2 p-3 mb-4 font-display focus:outline-none"
      />

      {q && (
        <div className="brutal p-4 mb-5 bg-[#FFD700]" data-testid="draw-search-results">
          <h2 className="font-display font-black uppercase tracking-tight mb-2">
            {matches.length ? `Found ${matches.length}` : "No competitor found"}
          </h2>
          {matches.map((m, i) => (
            <div key={i} className="brutal-2 bg-white p-2 mb-2 flex items-center gap-2" data-testid="draw-search-match">
              <JerseyBadge jersey={m.jersey} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-black uppercase truncate">{m.name}</div>
                <div className="font-display text-[11px] uppercase tracking-widest text-gray-500">{m.division} · {m.round} · {m.heat}</div>
              </div>
              {m.status === "live" && <span className="bg-[#E63946] text-white text-[9px] font-black uppercase px-2 py-0.5">Live</span>}
            </div>
          ))}
          {matches.length > 0 && (
            <p className="font-display text-xs mt-1">Wear your <strong>jersey color</strong> and check in with the beach marshal for your heat.</p>
          )}
        </div>
      )}

      <ScheduleItinerary />

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 mt-6" data-testid="draw-division-tabs">
        {data.divisions.map((d) => (
          <button
            key={d.id}
            data-testid={`draw-tab-${d.id}`}
            onClick={() => setTab(d.id)}
            className={`brutal-2 whitespace-nowrap px-3 py-2 font-display font-black uppercase text-xs tracking-widest ${
              tab === d.id ? "bg-black text-white" : "bg-white"
            }`}
          >
            {d.name}
          </button>
        ))}
      </div>

      {!active || active.rounds.length === 0 ? (
        <div className="brutal p-8 text-center" data-testid="draw-empty">
          <div className="font-score text-3xl">NO DRAW YET</div>
          <p className="font-display text-gray-500 uppercase text-xs tracking-widest mt-2">Generate heats in Setup</p>
        </div>
      ) : (
        active.rounds.map((rnd) => (
          <div key={rnd.id} className="mb-6" data-testid={`draw-round-${rnd.id}`}>
            <h2 className="font-display text-xl font-black uppercase tracking-tight border-b-4 border-black pb-1 mb-3">
              {rnd.name}
            </h2>
            <div className="space-y-3">
              {rnd.heats.map((h) => (
                <div key={h.id} className={`brutal p-3 ${h.is_live ? "border-[#E63946]" : ""}`} data-testid={`draw-heat-${h.id}`}>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-black uppercase">{h.name}</span>
                      {h.is_final && <span className="bg-[#FFD700] text-black text-[10px] font-black uppercase px-2 py-0.5 tracking-widest">Final</span>}
                      {h.is_on_deck && <span className="brutal-2 text-[10px] font-black uppercase px-2 py-0.5 tracking-widest">On Deck</span>}
                    </div>
                    <StatusBadge status={h.status} isFinal={h.is_final} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {h.riders.map((r) => (
                      <div key={r.rider_id} className="flex items-center gap-2">
                        <JerseyBadge jersey={r.jersey} size="sm" />
                        <span className="font-display font-bold uppercase text-sm truncate">{r.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
